/**
 * English language strings for the Vivatis PDB application.
 * All user-facing text should be defined here for easy localisation.
 */

const en = {
  /* ------------------------------------------------------------------ */
  /* Global / shared                                                    */
  /* ------------------------------------------------------------------ */
  common: {
    products: 'Products',
    product: 'product',
    productsPlural: 'products',
    partner: 'Partner',
    name: 'Name',
    category: 'Category',
    yes: 'Yes',
    no: 'No',
    back: 'Back',
    next: 'Next',
    skip: 'Skip',
    filter: 'Filter',
    filterMode: 'Filter mode',
    and: 'AND',
    or: 'OR',
    clearAll: 'Clear all',
    removeAll: 'Remove All',
    browseProducts: 'Browse Products',
    noProductsFound: 'No products found.',
    noResultsFor: (term: string) => `No results for “${term}”.`,
    new: 'NEW',
    pdf: 'PDF',
    viewAll: 'View all',
    offlineBanner: 'You are offline. Some features are restricted.',
    offlineTooltip: 'Not available in offline mode',
    offlineLanguageHint: 'Offline: UI language switches, product data stays in the previously loaded language until you are back online',
    pwaUpdateAvailable: 'A new version is available. Updating...',
    pwaInstallTitle: 'Install App',
    pwaInstallDescription: 'Add to your home screen for quick access.',
    pwaInstallButton: 'Install',
  },

  /* ------------------------------------------------------------------ */
  /* Navigation                                                         */
  /* ------------------------------------------------------------------ */
  nav: {
    home: 'Home',
    products: 'Products',
    wizard: 'Wizard',
    favorites: 'Favorites',
    compare: 'Compare',
    cart: 'Documents',
    searchPlaceholder: 'Search products...',
    toggleTheme: 'Toggle theme',
    logout: 'Logout',
    admin: 'Admin',
    toggleMenu: 'Toggle menu',
  },

  /* ------------------------------------------------------------------ */
  /* Branding                                                           */
  /* ------------------------------------------------------------------ */
  brand: {
    vivatis: 'VIVATIS',
    pdb: 'PDB',
    pharma: 'PHARMA',
    productDatabase: 'Product Database',
    copyright: 'Vivatis Pharma GmbH © 2026',
  },

  /* ------------------------------------------------------------------ */
  /* Login                                                              */
  /* ------------------------------------------------------------------ */
  login: {
    email: 'Email',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    signingIn: 'Signing in...',
    signIn: 'Sign In',
    forgotPassword: 'Forgot password?',
  },

  /* ------------------------------------------------------------------ */
  /* Home page                                                          */
  /* ------------------------------------------------------------------ */
  home: {
    welcomeBack: (name: string) => `Welcome back${name ? `, ${name}` : ''}!`,
    whatToDo: 'What would you like to do today?',
    stats: {
      products: 'Products',
      new: 'New',
      favorites: 'Favorites',
      inCart: 'In Document',
    },
    actions: {
      browseProducts: 'Browse Products',
      viewAllProducts: (count: number) => (count === 1 ? 'View 1 product' : `View all ${count} products`),
      browseProductsCount: (count: number) => `${count} products`,
      loading: 'Loading...',
      startWizard: 'Start Wizard',
      findMatching: 'Find matching products',
      favorites: 'Favorites',
      productsSaved: (count: number) => `${count} saved`,
      meetingCart: 'Meeting Document',
      productsInCart: (count: number) => `${count} in document`,
    },
    browseByIndication: 'Browse by Indication',
    topIndications: 'Your Top Indications',
    topIndicationsEmpty: 'Start browsing products to see your most viewed indications here.',
    newProducts: 'New Products',
    newProductsTooltip: 'All products added within the last 12 months are considered new.',
    newStudies: 'New Studies',
    newStudiesTooltip: 'Products with newly published clinical studies within the last 12 months.',
    newStudyBadge: 'New Study',
    publishedStudies: (count: number) => `${count} published clinical studies`,
    favoritesTitle: 'Favorites',
    noFavorites: 'No favorites yet. Click the heart icon on any product to save it here.',
    productCountLabel: (count: number) => `${count} ${count === 1 ? 'product' : 'products'}`,
    recentlyViewed: 'Recently Viewed',
    noRecentlyViewed: 'No products viewed yet. Start with the',
    productCatalog: 'Product Catalog',
    viewAll: 'View all',
  },

  /* ------------------------------------------------------------------ */
  /* Product list                                                       */
  /* ------------------------------------------------------------------ */
  productList: {
    sort: {
      relevance: 'Relevance',
      nameAsc: 'Name (A-Z)',
      nameDesc: 'Name (Z-A)',
      articleNumber: 'Article No.',
      partner: 'Partner',
      indication: 'Indication',
    },
    viewTable: 'Table',
    viewCards: 'Cards',
    productsFound: (count: number) => `${count} products found`,
    productCount: (count: number, total: number) => `${count} of ${total} products`,
    loading: 'Loading products...',
    adjustFilters: 'Adjust your filters.',
    resetFilters: 'Reset Filters',
  },

  /* ------------------------------------------------------------------ */
  /* Product search                                                     */
  /* ------------------------------------------------------------------ */
  productSearch: {
    placeholder: 'Search by name, article number, ingredient, indication...',
  },

  /* ------------------------------------------------------------------ */
  /* Product table                                                      */
  /* ------------------------------------------------------------------ */
  productTable: {
    articleNo: 'Article No.',
    name: 'Name',
    partner: 'Partner',
    indication: 'Indication',
    application: 'Application',
    country: 'Country',
    dailyDose: 'Daily Dose',
    claims: 'Claims',
    actions: 'Actions',
  },

  /* ------------------------------------------------------------------ */
  /* Product detail                                                     */
  /* ------------------------------------------------------------------ */
  productDetail: {
    tabs: {
      overview: 'Overview',
      productData: 'Product Data',
      moreDetails: 'Price',
    },
    notFound: 'Product not found',
    backToOverview: 'Back to Overview',
    marketingClaims: 'Marketing Claims',
    indications: 'Indications',
    applications: 'Applications',
    countries: 'Countries',
    description: 'Description',
    shortDescription: 'Short Description',
    website: 'Product Website',
    multimedia: 'Multimedia Website',
    studies: 'Studies',
    origin: 'Origin',
    dosage: 'Dosage',
    appearance: 'Appearance',
    category: 'Category',
    articleNumber: 'Article Number',
    active: 'Active',
    created: 'Created',
    showDailyPrice: 'Show daily price',
    perDay: '/ day',
    generatingPdf: 'Generating PDF...',
    sendViaEmail: 'Send via Email',
    emailSubject: (name: string, companyName: string) => `${companyName} — Product Data Sheet: ${name}`,
    emailBody: (name: string, userName: string, companyName: string) =>
      `Dear Customer,\n\nPlease find attached the product data sheet for ${name}.\n\nPlease let me know if you have any questions or need additional information.\n\nBest regards,\n${userName}\n${companyName}`,
    toastEmail: 'Email sent successfully!',
  },

  /* ------------------------------------------------------------------ */
  /* Actions (favorite, cart, compare)                                   */
  /* ------------------------------------------------------------------ */
  actions: {
    addToFavorites: 'Add to favorites',
    removeFromFavorites: 'Remove from favorites',
    favorited: 'Favorited',
    favorite: 'Favorite',
    addToCart: 'Add to document',
    removeFromCart: 'Remove from document',
    removeFromAllCarts: 'Remove from all documents',
    inCart: 'In document',
    addToCartLabel: 'Add to Document',
    inCartLabel: 'In Document',
    cartLabel: 'Documents',
    addToComparison: 'Add to comparison',
    removeFromComparison: 'Remove from comparison',
    inComparison: 'In comparison',
    compared: 'Compared',
    compare: 'Compare',
    manageFavorites: 'Manage favorites',
    addToFavoritesNoGroup: 'Add to favorites (no group)',
    favoritesLabel: 'Favorites',
  },

  /* ------------------------------------------------------------------ */
  /* Favorites page                                                     */
  /* ------------------------------------------------------------------ */
  favorites: {
    title: 'Favorites',
    noFavorites: 'No Favorites',
    noFavoritesDescription: 'Mark products as favorites to see them here.',
    deleteGroup: {
      title: 'Delete Group',
      message: (name: string) => `Are you sure you want to delete "${name}"?`,
      moveLabel: 'Move products to:',
      ungroupedOption: 'Not in a group (ungrouped)',
      confirm: 'Delete Group',
      cancel: 'Cancel',
    },
    browseProducts: 'Browse Products',
    dragProductsHint: 'Drag products here',
    notInGroup: 'Not in a group',
    notInGroupWithCount: (count: number) => `Not in a group (${count})`,
    dragCopyHint: '⌘/⇧ held — drop to copy to group',
    dragMoveHint: 'Drop to move · hold ⌘/⇧ to copy',
    addGroupToDocument: 'Add group to document',
    deleteGroupTitle: 'Delete group',
    groupNamePlaceholder: 'Group name...',
    create: 'Create',
    cancel: 'Cancel',
    newGroup: 'New Group',
    inDocument: 'In Document',
    alreadyInDocument: 'The products are already in the document.',
    addedToDocument: 'Products added to the document.',
    removedFromDocument: 'Products removed from the document.',
    addToDocument: 'Add',
    productCountLabel: (count: number) => `${count} ${count === 1 ? 'product' : 'products'}`,
    openProductTitle: 'Open',
  },

  /* ------------------------------------------------------------------ */
  /* Comparison page                                                    */
  /* ------------------------------------------------------------------ */
  comparison: {
    title: 'Product Comparison',
    loading: 'Loading products...',
    productCount: (count: number, max: number) => `(${count}/${max})`,
    noProducts: 'No Products to Compare',
    noProductsDescription: 'Add products to compare them side by side.',
    browseProducts: 'Browse Products',
    addProduct: 'Add Product',
    removeFromComparison: 'Remove from comparison',
    removeAll: 'Remove All',
    showDifferencesOnly: 'Show differences only',
    showingDifferencesOnly: 'Showing differences only',
    hideDailyPrice: 'Hide daily price',
    dailyPriceHidden: 'Daily price hidden',
    hidePrice: 'Hide price',
    priceHidden: 'Price hidden',
    allIdentical: 'All values are identical across these products.',
    rows: {
      articleName: 'Article Name',
      articleNumber: 'Article Number',
      partner: 'Partner',
      manufacturer: 'Manufacturer',
      category: 'Category',
      clinicalStudies: 'Clinical Studies',
      bioactiveIngredients: 'Bioactive Ingredients / Assay',
      indications: 'Indications',
      applications: 'Applications',
      countries: 'Countries',
      dosage: 'Dosage',
      dailyDosage: 'Daily Dosage',
      appearance: 'Appearance',
      origin: 'Origin',
      sourceOrigin: 'Source / Origin',
      marketingClaims: 'Marketing Claims',
      dailyPrice: 'Daily Price',
      price: 'Price',
      novelFood: 'Novel Food',
    },
    novelFoodStatuses: {
      yes: 'Yes',
      ongoing: 'Ongoing',
      approved: 'Approved',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Cart page                                                          */
  /* ------------------------------------------------------------------ */
  cart: {
    title: 'Meeting Document',
    subtitle: 'Products for your customer meeting',
    emptyTitle: 'Your document is empty',
    emptyDescription: 'Add products from the catalog.',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove',
    rename: 'Rename',
    lockedNotice: (date: string) => `This document was sent on ${date} and can no longer be changed.`,
    generatePdf: 'Generate PDF',
    generatingPdf: 'Generating PDF...',
    sendViaEmail: 'Send via Email',
    createOpportunity: 'Create Opportunity in SalesForce',
    clearCart: 'Clear Document',
    archiveDocument: 'Archive Document',
    confirmArchive: (name: string) => `Archive "${name}"? It will no longer appear in your document list and will only be visible to administrators afterwards.`,
    deleteDocument: 'Delete Document',
    confirmClear: 'Are you sure you want to clear the document?',
    confirmDelete: (name: string) => `Delete "${name}"? This will remove the document and all its products.`,
    toastPdf: 'PDF has been generated and downloaded.',
    toastPdfError: 'PDF generation failed.',
    toastEmail: 'Email sent successfully!',
    showDocuments: 'Show Documents',
    newDocument: 'New Document',
    searchDocuments: 'Search documents...',
    noDocumentsFound: 'No documents found.',
    items: 'items',
    comingSoon: 'Coming Soon',
    comingSoonDescription: 'The SalesForce integration is currently under development. This feature will be available in a future update.',
    productCount: (count: number) => `${count} ${count === 1 ? 'product' : 'products'}`,
    emailSubject: (count: number, companyName: string) => `${companyName} — Product Selection (${count} products)`,
    emailBody: (productList: string, userName: string, companyName: string) => `Dear Customer,\n\nPlease find below the product selection from our recent meeting:\n\n${productList}\n\nPlease let me know if you have any questions or need additional information on any of these products.\n\nBest regards,\n${userName}\n${companyName}`,
    email: {
      title: 'Send via Email',
      recipients: 'Recipients',
      subject: 'Subject',
      message: 'Message',
      placeholder: 'Enter email address...',
      cancel: 'Cancel',
      send: 'Send Email',
      sending: 'Sending...',
      invalidEmail: 'Please enter a valid email address.',
      duplicateEmail: 'This email address has already been added.',
      sendError: 'Failed to send email. Please try again.',
      products: 'Products',
      productsCount: (selected: number, total: number) => `${selected} of ${total}`,
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      noProductsWarning: 'Select at least one product to include in the email.',
      fileTypesLegend: 'Attach documents',
      fileTypesLoading: 'Loading available documents...',
      fileTypes: {
        study: 'Studies',
        marketing: 'Marketing documents',
        quality: 'Quality documents',
      },
      qualityHint: 'Quality documents are not attached. {email} sends them to the recipients separately.',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Settings                                                           */
  /* ------------------------------------------------------------------ */
  settings: {
    title: 'Settings',
    subtitle: 'Configure your application preferences',
    multiCart: {
      title: 'Multi Document',
      description:
        'Enable multiple meeting documents to organize products for different customers or meetings. You can have up to 3 documents and switch between them from the header.',
    },
    favoritesOnHome: {
      title: 'Favorites on Dashboard',
      description: 'Show your favorites section on the home screen for quick access to saved products.',
    },
    favoriteGroups: {
      title: 'Favorite Groups',
      description: 'Organize your favorites into groups. You can create up to 5 groups and drag products between them. Each group can be added to a document with one click.',
    },
    productData: {
      title: 'Product Data',
      description: 'Shows when the product data was last loaded from the server.',
      lastLoaded: 'Last loaded',
      never: 'Never loaded yet',
      reload: 'Reload products',
      reloading: 'Reloading...',
      reloadSuccess: 'Product data reloaded',
      reloadError: 'Product data could not be reloaded',
    },
    changePassword: {
      title: 'Change Password',
      description: 'Update the password you use to sign in.',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm New Password',
      submit: 'Update Password',
      submitting: 'Updating...',
      success: 'Your password has been updated.',
      passwordMismatch: 'Passwords do not match',
      error: 'Could not update password',
      backToSettings: 'Back to Settings',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Wizard                                                             */
  /* ------------------------------------------------------------------ */
  wizard: {
    steps: {
      country: 'Country',
      application: 'Application',
      indication: 'Indication',
    },
    selectCountry: 'Select Country',
    selectCountrySubtitle: 'In which countries should the product be available?',
    selectApplication: 'Select Application',
    selectApplicationSubtitle: 'Which dosage form do you prefer?',
    selectIndication: 'Select Indication',
    selectIndicationSubtitle: 'Which health areas are relevant?',
    showResults: 'Show Results',
    noSelection: 'No selection made',
    selected: (count: number) => `${count} selected`,
    filterAnd: 'And',
    filterOr: 'Or',
    filterAndExplanation: 'Products must match all selected criteria',
    filterOrExplanation: 'Products must match at least one criterion per step',
  },

  /* ------------------------------------------------------------------ */
  /* Filter sidebar                                                     */
  /* ------------------------------------------------------------------ */
  filterSidebar: {
    title: 'Filter',
    category: 'Category',
    country: 'Country',
    application: 'Application',
    indication: 'Indication',
    partner: 'Partner',
    marketingClaims: 'Marketing Claims',
    agreement: 'Agreement',
    agreements: {
      DA: 'DA — Distribution Agreement',
      CPA: 'CPA — Customer Protection',
      Gentleman: 'Gentleman Agreement',
      LOI: 'LOI — Letter of Intent',
      none: 'No agreement',
    },
    agreementDescriptions: {
      DA: 'Distribution Agreement (exclusive)',
      CPA: 'Customer Protection Agreement (non-exclusive)',
      Gentleman: 'Gentleman Agreement (verbal, no contract)',
      LOI: 'Letter of Intent (preliminary stage of DA)',
      none: 'No agreement signed with this country yet',
    },
    newProducts: 'New Products',
    newStudy: 'New Study',
    restrictedTo: (territories: string) => `Your view is limited to ${territories}.`,
  },

  /* ------------------------------------------------------------------ */
  /* Marketing claim descriptions                                       */
  /* ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------ */
  /* Not Found (404)                                                    */
  /* ------------------------------------------------------------------ */
  notFound: {
    title: '404',
    heading: 'Page Not Found',
    description: 'The page you are looking for does not exist or has been moved.',
    goBack: 'Go Back',
    home: 'Home',
  },

  /* ------------------------------------------------------------------ */
  /* Forgot Password                                                    */
  /* ------------------------------------------------------------------ */
  forgotPassword: {
    subtitle: 'Reset your password',
    email: 'Email',
    emailPlaceholder: 'your@email.com',
    submit: 'Send Reset Link',
    submitting: 'Sending...',
    success: 'We have sent a password reset link to your email address.',
    backToLogin: 'Back to Login',
    error: 'Could not send reset link',
  },

  /* ------------------------------------------------------------------ */
  /* Reset Password                                                     */
  /* ------------------------------------------------------------------ */
  resetPassword: {
    subtitle: 'Set a new password',
    email: 'Email',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    submit: 'Reset Password',
    submitting: 'Resetting...',
    passwordMismatch: 'Passwords do not match',
    backToLogin: 'Back to Login',
    error: 'Could not reset password',
  },

  /* ------------------------------------------------------------------ */
  /* Marketing claim descriptions                                       */
  /* ------------------------------------------------------------------ */
  claimDescriptions: {
    Vegan: 'Contains no animal-derived ingredients or by-products.',
    Organic: 'Certified organic — grown without synthetic pesticides or fertilizers.',
    'Non-GMO': 'Free from genetically modified organisms.',
    'Clinically Studied': 'Efficacy supported by published clinical trials.',
    Patented: 'Protected by one or more patents for unique formulation or process.',
    Halal: 'Certified Halal — compliant with Islamic dietary guidelines.',
    Kosher: 'Certified Kosher — compliant with Jewish dietary laws.',
    'Gluten-Free': 'Contains no gluten — safe for people with celiac disease or gluten sensitivity.',
  },
  /* ------------------------------------------------------------------ */
  /* PDF export                                                         */
  /* ------------------------------------------------------------------ */
  pdf: {
    productDataSheet: 'Product Data Sheet',
    productDataSheets: 'Product Data Sheets',
    keyFacts: 'Key Facts',
    dailyDosage: 'Daily Dosage',
    appearance: 'Appearance',
    bioactiveIngredient: 'Bioactive Ingredient',
    indications: 'Indications',
    possibleApplications: 'Possible Applications',
    description: 'Description',
    linkStudies: 'Studies',
    origin: 'Origin',
    confidential: 'Confidential -- For business use only',
    productCountOne: ':count product',
    productCountOther: ':count products',
    clinicalStudies: 'Clinical Studies',
    marketingClaims: 'Marketing Claims',
    territories: 'Territories',
    moq: 'MOQ in kg',
    shelfLife: 'Shelf Life',
    shelfLifeValue: ':months months',
    novelFoodStatus: 'Novel Food',
    badgeNew: 'NEW',
    badgeNewStudy: 'NEW STUDY',
    contactPerson: 'Your contact at Vivatis:',
    website: 'Product Website',
    multimedia: 'Multimedia Website',
  },
} as const;

export default en;

type Stringify<T> = T extends (...args: infer A) => string
  ? (...args: A) => string
  : T extends string
    ? string
    : { [K in keyof T]: Stringify<T[K]> };

/** Structural type for any language file — keys enforced, values allow any string */
export type LangStrings = Stringify<typeof en>;
